import {
  CreativeTagEvent,
  ViewabilityEnvironment,
  ViewabilityEventConditions
} from '../enums';
import { EventEmitter } from '../types';
import { ViewabilityObserver } from '../types/viewability-observer';

export class DisplayEventNotifier {
  private isEventEmitted = false;
  private isViewable = false;
  private isReady = true;
  private displayEvent?: Event;

  constructor(
    observer: ViewabilityObserver,
    private readonly emitter: EventEmitter<CreativeTagEvent>,
    source?: EventEmitter<CreativeTagEvent>
  ) {
    // Without a `source` (static `image` path, meta-dsp) the notifier is the
    // origin of the event: readiness is implied and 50% visibility is the only
    // trigger — byte-identical to before.
    //
    // With a `source` (rich path) the engine owns the Display milestone and its
    // event carries the `/ocs` payload, so this becomes an AND-gate: keep the
    // engine event, release it once the creative is really on screen. In Web
    // the engine reaches that milestone only on a creative it actually
    // rendered, so the viewability side is eager. In InApp the render is eager
    // and also runs on SDK pre-loads that are never shown, so MRAID exposure is
    // the only signal that separates a real display from a pre-load
    // (ADV-30177).
    if (source) {
      this.isReady = false;
      this.isViewable = observer.environment !== ViewabilityEnvironment.InApp;
      source.addEventListener(CreativeTagEvent.CreativeDisplay, evt => {
        this.isReady = true;
        this.displayEvent = evt;
        this.tryEmit();
      });
    }

    observer.on(ViewabilityEventConditions.On50PercentVisible, () =>
      this.handleEvent()
    );

    this.tryEmit();
  }

  public handleEvent() {
    this.isViewable = true;
    this.tryEmit();
  }

  private tryEmit() {
    if (this.isEventEmitted || !this.isReady || !this.isViewable) {
      return;
    }
    this.isEventEmitted = true;
    if (this.displayEvent !== undefined) {
      this.emitter.emit(CreativeTagEvent.CreativeDisplay, this.displayEvent);
    } else {
      this.emitter.emit(CreativeTagEvent.CreativeDisplay);
    }
  }
}
